<template>
  <WdsAlert v-if="visible" warning bordered data-test="pending-contract-acceptance-alert">
    <WdsStack gap-md>
      <WdsStack direction-column>
        <span>{{ headline }}</span>
        <WdsText :text="subtext" />
      </WdsStack>
      <WdsStack align-center>
        <WdsTooltip :text="tooltipText" :disabled="!tooltipText" global>
          <WdsButton
            class="tw-min-w-max"
            :disabled="!canAct"
            primary
            small
            :data-test="
              isMspConfirmation
                ? 'pending-contract-acceptance-confirm-engagement'
                : 'pending-contract-acceptance-review-contract'
            "
            @click="handleClick"
          >
            {{ buttonLabel }}
          </WdsButton>
        </WdsTooltip>
      </WdsStack>
    </WdsStack>
    <ConfirmEngagementModal
      v-if="isMspConfirmation"
      :open="showConfirmModal"
      :hire-id="hireId"
      @close="showConfirmModal = false"
    />
  </WdsAlert>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { useContractDrawer } from '@/components/Hire/composables/useContractDrawer'
import { useViewerSignsContract, useViewerConfirmsContract } from '@/components/Hire/composables/useViewerSignsContract'
import ConfirmEngagementModal from '@/components/Hire/ConfirmEngagementModal.vue'
import { graphql } from '@/gql'
import { HireActiveStatus } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

/**
 * Shared alert shown when a contract is waiting for the viewer to accept it.
 * Covers two cases:
 *   - Initial offer: the hire is in Offered status and needs first-time
 *     acceptance.
 *   - Amendment: the hire is already active and the client has edited the
 *     contract.
 *
 * Two action variants depending on who is viewing:
 *   - MSP: opens ConfirmEngagementModal ("Confirm engagement").
 *   - Worker / SA: opens the contract drawer in accept mode.
 *
 * The client view is informational and is not handled here. The parent
 * status components keep their own "Pending signature" text.
 */
const props = defineProps<{ hireId: string }>()

const showConfirmModal = ref(false)

const { result } = useQuery(
  graphql(`
    query PendingContractAcceptanceData($id: ID!) {
      hire(id: $id) {
        id
        activeStatus
        pendingContractChanges
        viewerCanAcceptContract
        recruiterManagesWorkers
        viewerIsSupplier
        supplierSignatureWaived
        supplierHire {
          id
          activeStatus
        }
      }
    }
  `),
  { id: props.hireId },
  { clientId: 'publicApi' },
)

const { hasWorkerCapabilities } = useAccountCapabilities()
const { viewerIsSupplier, hasRecruiterManagingWorkers } = useHireRecruiter(props.hireId)
const viewerSignsContract = useViewerSignsContract()
const viewerConfirmsContract = useViewerConfirmsContract()

const hire = computed(() => result.value?.hire)
const isInitialOffer = computed(() => hire.value?.activeStatus === HireActiveStatus.Offered)
const isAmendment = computed(() => hire.value?.pendingContractChanges ?? false)

const isMspConfirmation = computed(
  () =>
    viewerIsSupplier.value
    && hasRecruiterManagingWorkers.value
    && hire.value?.supplierHire != null,
)
// Only show the worker/SA branch when the viewer actually confirms the
// contract — by signing, or via a signature-free supplier confirmation
// (supplierSignatureWaived). A staffing agency attributed for a referral fee
// (not managing the worker) has no contract to confirm here, so this branch
// must not fire for them.
const isWorkerOrSa = computed(
  () =>
    hasWorkerCapabilities.value
    && !isMspConfirmation.value
    && viewerConfirmsContract(hire.value ?? {}),
)

// The viewer must act, but with an accept action instead of a signature.
const signatureWaived = computed(
  () => isWorkerOrSa.value && !viewerSignsContract(hire.value ?? {}),
)

const visible = computed(
  () =>
    (isInitialOffer.value || isAmendment.value)
    && (isMspConfirmation.value || isWorkerOrSa.value),
)

const canAcceptContract = computed(() => hire.value?.viewerCanAcceptContract ?? false)

// On an initial offer, the MSP can only confirm once the worker has accepted
// the contract on the supplier-mirror hire. For amendments this rule doesn't
// apply — the MSP confirms the client's change first, then amends the
// supplier-mirror separately.
const supplierHireAccepted = computed(() => {
  const status = hire.value?.supplierHire?.activeStatus
  return (
    status != null
    && status !== HireActiveStatus.Draft
    && status !== HireActiveStatus.Offered
  )
})

const canAct = computed(() => {
  if (isMspConfirmation.value && isInitialOffer.value) {
    return canAcceptContract.value && supplierHireAccepted.value
  }
  return canAcceptContract.value
})

const tooltipText = computed(() => {
  if (canAct.value) return ''
  if (isMspConfirmation.value && isInitialOffer.value && !supplierHireAccepted.value) {
    return $t('The worker must accept the engagement before you can confirm.')
  }
  return $t('One or more required steps have not yet been completed.')
})

const headline = computed(() => {
  if (isMspConfirmation.value) {
    return isAmendment.value
      ? $t('Engagement changes need your confirmation')
      : $t('Engagement needs your confirmation')
  }
  if (signatureWaived.value) {
    return isAmendment.value
      ? $t('Contract changes need your acceptance')
      : $t('Contract needs your acceptance')
  }
  return isAmendment.value
    ? $t('Contract changes need your signature')
    : $t('Contract needs your signature')
})

const subtext = computed(() => {
  if (isMspConfirmation.value) {
    if (isAmendment.value) {
      return $t('Before confirming here, amend the supplier version of the hire and wait for the worker or supplier to accept the change there.')
    }
    return supplierHireAccepted.value
      ? $t('The worker or staffing agency has accepted the supplier hire. Review the details and confirm the engagement.')
      : $t('The worker or staffing agency must accept the supplier hire before you can confirm.')
  }
  if (signatureWaived.value) {
    return isAmendment.value
      ? $t('Review the changes and accept the updated contract.')
      : $t('Accept the contract to confirm the offer and start your engagement.')
  }
  return isAmendment.value
    ? $t('Review the changes and sign the updated contract.')
    : $t('Sign the contract to confirm the offer and start your engagement.')
})

const buttonLabel = computed(() =>
  isMspConfirmation.value ? $t('Confirm engagement') : $t('Review contract'),
)

const { openContractDrawer } = useContractDrawer()

const handleClick = () => {
  if (isMspConfirmation.value) {
    showConfirmModal.value = true
  } else {
    openContractDrawer('accept')
  }
}
</script>
